import React from "react";
import { useTranslation } from "react-i18next";
import { v4 as uuidv4 } from "uuid";

import {
  StyledHeader,
  StyledTrigger,
  StyledChevron,
  StyledAccordionTitle,
  StyledAccordionSubtitle,
  HeaderComponentContainer,
  StyledItem,
  StyledContent,
  StyledAccordion,
  AccordionContentContainer,
  AccordionContentItem,
  AccordionContentItemKey,
  AccordionContentItemValue,
} from "./AccordionList.styles";

type AccordionListHeaderProps = IAccordionList;

export interface IAccordionList {
  title: string | React.ReactNode;
  subtitle: string;
  index?: number;
  children: React.ReactNode;
}

export interface IAccordionListItemProps extends IAccordionList {
  headerComponents?: React.ReactNode;
  value: string;
}

function AccordionListHeader({
  title,
  index,
  subtitle,
  children,
}: AccordionListHeaderProps) {
  const isEvenElemet = () => Boolean(index && index % 2 === 0);

  return (
    <StyledHeader>
      <StyledTrigger customColor={isEvenElemet()}>
        <div style={{ display: "flex" }}>
          <StyledChevron aria-hidden />

          <div>
            <StyledAccordionTitle>{title}</StyledAccordionTitle>
            <StyledAccordionSubtitle>{subtitle}</StyledAccordionSubtitle>
          </div>
        </div>

        <HeaderComponentContainer>{children}</HeaderComponentContainer>
      </StyledTrigger>
    </StyledHeader>
  );
}

export interface IAccordionListItemDefaultContentProps {
  content: any;
  /**
   * `key` is the English fallback and `value` the row field; `labelKey` is what the reader sees
   * when the caller has one (EN-5191). `labelParams` feeds that key's interpolation — the ward
   * term is configured per hospital ("Ward", "Cohort"), so a label carrying it cannot be a
   * static string in either language.
   */
  columns: {
    key: string;
    labelKey?: string;
    labelParams?: Record<string, string>;
    value: string;
  }[];
}

export function AccordionListItemDefaultContent({
  content,
  columns,
}: IAccordionListItemDefaultContentProps) {
  const { t } = useTranslation();

  return (
    <AccordionContentContainer>
      {columns.map((column) => (
        <AccordionContentItem key={uuidv4()}>
          <AccordionContentItemKey>
            {column.labelKey
              ? t(column.labelKey, column.labelParams)
              : column.key}
          </AccordionContentItemKey>

          <AccordionContentItemValue>
            {content[column.value]}
          </AccordionContentItemValue>
        </AccordionContentItem>
      ))}
    </AccordionContentContainer>
  );
}

export function AccordionListItem({
  title,
  subtitle,
  headerComponents,
  children,
  index,
  value,
  ...props
}: IAccordionListItemProps) {
  return (
    <StyledItem value={value} {...props}>
      <AccordionListHeader index={index} title={title} subtitle={subtitle}>
        {headerComponents}
      </AccordionListHeader>

      <StyledContent>{children}</StyledContent>
    </StyledItem>
  );
}

export const AccordionList = StyledAccordion;
